import { useState, useEffect, useCallback } from 'react';
import { usersAPI } from '../../services/api';
import { Table, Badge, Button, Modal, Input, Select, Pagination } from '../../components/ui';

const ROLE_DESCRIPTIONS = {
  super_admin:     { desc: 'Full system access',           color: '#f06060' },
  platform_admin:  { desc: 'Pages & navigation',           color: '#e8a43a' },
  content_manager: { desc: 'Publish & manage content',     color: '#4e9cf5' },
  editor:          { desc: 'Create & edit, no publish',    color: '#a78bfa' },
  contributor:     { desc: 'Draft submissions only',       color: '#f472b6' },
  trainer:         { desc: 'Cohort trainer portal access', color: '#3ecf8e' },
  partner:         { desc: 'Partner institution portal',   color: '#2dd4bf' },
  student:         { desc: 'SPA learner account',          color: '#94a3b8' },
};

export default function UsersPage() {
  const [users, setUsers]             = useState([]);
  const [roles, setRoles]             = useState([]);
  const [pagination, setPag]          = useState(null);
  const [loading, setLoading]         = useState(true);
  const [page, setPage]               = useState(1);
  const [search, setSearch]           = useState('');
  const [filterRole, setFilterRole]   = useState('');
  const [modal, setModal]             = useState(null);
  const [selected, setSel]            = useState(null);
  const [saving, setSaving]           = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [waPhone, setWaPhone]         = useState('');
  const [form, setForm]               = useState({ name: '', email: '', phone: '', password: '', role_id: '', status: 'active' });

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await usersAPI.list({ page });
      setUsers(res.data.data || []);
      setPag(res.data.pagination);
    } catch { setUsers([]); } finally { setLoading(false); }
  }, [page]);

  useEffect(() => { loadUsers(); }, [loadUsers]);
  useEffect(() => { usersAPI.roles().then(r => setRoles(r.data.data || [])).catch(() => {}); }, []);

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789@#!';
    return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  };

  const openCreate = () => {
    const defaultRole = roles.find(r => r.name === 'student')?.id || roles[0]?.id || '';
    setForm({ name: '', email: '', phone: '', password: generatePassword(), role_id: defaultRole, status: 'active' });
    setSel(null); setModal('create');
  };

  const openEdit = (u) => {
    setForm({ name: u.name, email: u.email, phone: u.phone || '', password: '', role_id: roles.find(r => r.name === u.role)?.id || '', status: u.status });
    setSel(u); setModal('edit');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (modal === 'create') {
        await usersAPI.create(form);
        const roleName = roles.find(r => r.id === form.role_id)?.name || '';
        if (['trainer','partner','student'].includes(roleName)) {
          setCredentials({ name: form.name, email: form.email, phone: form.phone, password: form.password, role: roleName });
          setWaPhone(form.phone || '');
        }
        setModal(null); loadUsers();
      } else {
        await usersAPI.update(selected.id, { name: form.name, role_id: form.role_id, status: form.status });
        setModal(null); loadUsers();
      }
    } catch (err) { alert(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const spaUrl = window.location.origin.replace('3001', '3000');

  const buildMessage = () =>
    `*MilleHills Platform Access*\n\nHi ${credentials?.name}!\n\nYour account has been created on the MilleHills platform.\n\n📧 Email: ${credentials?.email}\n🔑 Password: ${credentials?.password}\n🔗 Login: ${spaUrl}/login\n\n_Please change your password after first login._\n\n— MilleHills Team`;

  const copyCredentials = () => {
    navigator.clipboard.writeText(
      `MilleHills Platform Access\n\nName: ${credentials?.name}\nEmail: ${credentials?.email}\nPassword: ${credentials?.password}\nRole: ${credentials?.role}\nLogin: ${spaUrl}/login\n\nPlease change your password after first login.`
    ).then(() => alert('Credentials copied!'));
  };

  const sendWhatsApp = () => {
    const phone = waPhone.replace(/\D/g, '');
    const text  = encodeURIComponent(buildMessage());
    const url   = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, '_blank');
  };

  const filtered = users.filter(u => {
    const matchSearch = !search || u.name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole   = !filterRole || u.role === filterRole;
    return matchSearch && matchRole;
  });

  const COLUMNS = [
    { key: 'name', label: 'User', render: (v, row) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 30, height: 30, borderRadius: '50%', background: `${ROLE_DESCRIPTIONS[row.role]?.color || 'var(--accent)'}18`, border: `1px solid ${ROLE_DESCRIPTIONS[row.role]?.color || 'var(--accent)'}44`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: ROLE_DESCRIPTIONS[row.role]?.color || 'var(--accent)', fontFamily: 'var(--font-display)', flexShrink: 0 }}>
          {v?.[0]?.toUpperCase()}
        </div>
        <div>
          <div style={{ fontWeight: 500 }}>{v}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.email}</div>
        </div>
      </div>
    )},
    { key: 'role', label: 'Role', render: v => (
      <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4, background: `${ROLE_DESCRIPTIONS[v]?.color || '#888'}15`, color: ROLE_DESCRIPTIONS[v]?.color || '#888', textTransform: 'capitalize' }}>
        {v?.replace(/_/g, ' ')}
      </span>
    )},
    { key: 'status',     label: 'Status',    render: v => <Badge status={v} /> },
    { key: 'last_login', label: 'Last Login', render: v => v ? new Date(v).toLocaleDateString() : <span style={{ color: 'var(--text-muted)' }}>Never</span> },
    { key: 'created_at', label: 'Created',    render: v => new Date(v).toLocaleDateString() },
    { key: '_actions',   label: '',           render: (_, row) => (
      <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); openEdit(row); }}>Edit</Button>
    )},
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, marginBottom: 2 }}>{pagination?.total ?? '—'} Users</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Role-based access control per Ecosystem Constitution</div>
        </div>
        <Button onClick={openCreate}>+ New User</Button>
      </div>

      {/* Role reference cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
        {Object.entries(ROLE_DESCRIPTIONS).map(([name, { desc, color }]) => (
          <div key={name} onClick={() => setFilterRole(filterRole === name ? '' : name)}
            style={{ background: filterRole === name ? `${color}12` : 'var(--bg-surface)', border: `1px solid ${filterRole === name ? color + '44' : 'var(--border)'}`, borderRadius: 'var(--radius-md)', padding: '10px 12px', cursor: 'pointer', transition: 'all 0.15s' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color, textTransform: 'capitalize', marginBottom: 2 }}>{name.replace(/_/g, ' ')}</div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{desc}</div>
          </div>
        ))}
      </div>

      {/* Search bar */}
      <div style={{ display: 'flex', gap: 10 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…"
          style={{ flex: 1, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', padding: '8px 12px', fontSize: 13 }} />
        {filterRole && <Button variant="secondary" onClick={() => setFilterRole('')}>Clear: {filterRole.replace(/_/g, ' ')}</Button>}
      </div>

      {/* Table */}
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        <Table columns={COLUMNS} data={filtered} loading={loading} onRowClick={openEdit} />
        <Pagination pagination={pagination} onPage={setPage} />
      </div>

      {/* Create / Edit Modal */}
      <Modal open={!!modal} onClose={() => setModal(null)} title={modal === 'create' ? 'New User' : `Edit — ${selected?.name}`}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {modal === 'create' && (
            <div style={{ padding: '10px 14px', background: 'rgba(232,164,58,0.08)', border: '1px solid rgba(232,164,58,0.25)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              For <strong>trainer</strong> or <strong>partner</strong> accounts a credential dialog will appear so you can share login details directly via WhatsApp.
            </div>
          )}

          <Input label="Full Name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          <Input label="Email Address" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} disabled={modal === 'edit'} />

          {modal === 'create' && (
            <Input label="Phone (for WhatsApp)" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+250 7xx xxx xxx" />
          )}

          {modal === 'create' && (
            <div>
              <Input label="Password" type="text" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} style={{ fontFamily: 'monospace' }} />
              <button onClick={() => setForm(f => ({ ...f, password: generatePassword() }))} style={{ fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', marginTop: 4 }}>
                ↺ Generate new password
              </button>
            </div>
          )}

          <Select label="Role" value={form.role_id} onChange={e => setForm(f => ({ ...f, role_id: e.target.value }))}>
            {roles.map(r => (
              <option key={r.id} value={r.id}>
                {r.name.replace(/_/g, ' ')}{ROLE_DESCRIPTIONS[r.name] ? ` — ${ROLE_DESCRIPTIONS[r.name].desc}` : ''}
              </option>
            ))}
          </Select>

          {modal === 'edit' && (
            <Select label="Status" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </Select>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 8 }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{modal === 'create' ? 'Create User' : 'Save Changes'}</Button>
          </div>
        </div>
      </Modal>

      {/* Credentials dialog */}
      <Modal open={!!credentials} onClose={() => setCredentials(null)} title="Share Login Credentials" width={500}>
        {credentials && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Credentials card */}
            <div style={{ padding: '16px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, fontFamily: 'monospace', fontSize: 13, lineHeight: 2.1 }}>
              <div><span style={{ color: 'var(--text-muted)' }}>Name:     </span><strong>{credentials.name}</strong></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Email:    </span><strong>{credentials.email}</strong></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Password: </span><strong style={{ color: 'var(--accent)', letterSpacing: '0.05em' }}>{credentials.password}</strong></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Role:     </span><strong style={{ textTransform: 'capitalize' }}>{credentials.role}</strong></div>
              <div><span style={{ color: 'var(--text-muted)' }}>Login:    </span><strong style={{ color: 'var(--blue-light)' }}>{spaUrl}/login</strong></div>
            </div>

            {/* WhatsApp phone input */}
            <div>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.03em', display: 'block', marginBottom: 6 }}>
                Send via WhatsApp
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={waPhone}
                  onChange={e => setWaPhone(e.target.value)}
                  placeholder="+250 7xx xxx xxx"
                  style={{ flex: 1, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', padding: '9px 12px', fontSize: 13 }}
                />
                <button onClick={sendWhatsApp} style={{ padding: '9px 18px', borderRadius: 'var(--radius-sm)', background: '#25D366', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>
                  Send →
                </button>
              </div>
              <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                {waPhone ? `Will send to ${waPhone}` : 'No number entered — will open WhatsApp without a recipient'}
              </p>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <Button onClick={copyCredentials} variant="secondary" style={{ flex: 1, justifyContent: 'center' }}>◈ Copy All</Button>
              <Button onClick={() => setCredentials(null)} style={{ flex: 1, justifyContent: 'center' }}>Done</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}